import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  apiErrorMessage,
  cloudBindHardwareAsset,
  cloudDeleteHardwareAsset,
  cloudListHardwareAssets,
  cloudUnbindHardwareByProject,
  cloudUpsertHardwareAsset,
  type HardwareAssetDto,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { HardwareAsset } from "./mock";

interface HardwareState {
  items: HardwareAsset[];
  unbound: HardwareAsset[];
  syncing: boolean;
  upsert: (item: HardwareAsset) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  bindToProject: (
    hardwareId: string,
    projectId: string,
    projectName: string,
  ) => Promise<HardwareAsset | undefined>;
  unbindFromProject: (projectId: string) => Promise<void>;
  getById: (id: string) => HardwareAsset | undefined;
  refresh: () => Promise<void>;
}

const HardwareContext = createContext<HardwareState | null>(null);

function mapDto(d: HardwareAssetDto): HardwareAsset {
  return {
    id: d.id,
    name: d.name,
    vendor: d.vendor,
    os: d.os,
    cpu: d.cpu,
    ram: d.ram,
    storage: d.storage,
    note: d.note,
    imageHint: d.imageHint,
    fromCatalog: d.fromCatalog,
    linkedProjectId: d.linkedProjectId,
    linkedProjectName: d.linkedProjectName,
  };
}

export function HardwareProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<HardwareAsset[]>([]);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setSyncing(true);
    try {
      const list = await cloudListHardwareAssets();
      setItems(list.map(mapDto));
    } catch {
      /* keep */
    } finally {
      setSyncing(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<HardwareState>(() => {
    const getById = (id: string) => items.find((x) => x.id === id);
    return {
      items,
      unbound: items.filter((item) => !item.linkedProjectId),
      syncing,
      getById,
      refresh,
      upsert: async (item) => {
        try {
          const saved = await cloudUpsertHardwareAsset({
            id: item.id,
            name: item.name,
            vendor: item.vendor,
            os: item.os,
            cpu: item.cpu,
            ram: item.ram,
            storage: item.storage,
            note: item.note,
            imageHint: item.imageHint,
            fromCatalog: item.fromCatalog,
            linkedProjectId: item.linkedProjectId,
            linkedProjectName: item.linkedProjectName,
          });
          const mapped = mapDto(saved);
          setItems((prev) => {
            const idx = prev.findIndex((x) => x.id === mapped.id);
            if (idx < 0) return [mapped, ...prev];
            const next = [...prev];
            next[idx] = mapped;
            return next;
          });
          return true;
        } catch (err) {
          console.error(apiErrorMessage(err, "保存硬件失败"));
          return false;
        }
      },
      remove: async (id) => {
        try {
          await cloudDeleteHardwareAsset(id);
          setItems((prev) => prev.filter((x) => x.id !== id));
          return true;
        } catch (err) {
          const msg = apiErrorMessage(
            err,
            "删除硬件失败",
          );
          console.error(msg);
          throw Object.assign(err instanceof Error ? err : new Error(msg), {
            message: msg,
          });
        }
      },
      bindToProject: async (hardwareId, projectId, projectName) => {
        try {
          const saved = await cloudBindHardwareAsset(
            hardwareId,
            projectId,
            projectName,
          );
          const mapped = mapDto(saved);
          setItems((prev) =>
            prev.map((x) => (x.id === hardwareId ? mapped : x)),
          );
          return mapped;
        } catch (err) {
          console.error(apiErrorMessage(err, "绑定失败"));
          return getById(hardwareId);
        }
      },
      unbindFromProject: async (projectId) => {
        try {
          await cloudUnbindHardwareByProject(projectId);
          setItems((prev) =>
            prev.map((x) =>
              x.linkedProjectId === projectId
                ? {
                    ...x,
                    linkedProjectId: undefined,
                    linkedProjectName: undefined,
                  }
                : x,
            ),
          );
        } catch (err) {
          console.error(apiErrorMessage(err, "解绑失败"));
        }
      },
    };
  }, [items, syncing, refresh]);

  return (
    <HardwareContext.Provider value={value}>
      {children}
    </HardwareContext.Provider>
  );
}

export function useHardware() {
  const ctx = useContext(HardwareContext);
  if (!ctx) {
    throw new Error("useHardware must be used within HardwareProvider");
  }
  return ctx;
}
